import { ViewChild, Component, Input, OnInit, signal, WritableSignal, ChangeDetectionStrategy } from "@angular/core";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { Resource } from "../../../pojo/Resource";
import { Table } from "../../s25-table/Table";
import { Bind } from "../../../decorators/bind.decorator";
import { S25EditableDateComponent } from "../../s25-editable/s25-editable-date/s25.editable.date.component";
import { S25EditableNumberComponent } from "../../s25-editable/s25-editable-number/s25.editable.number.component";
import { S25IconButtonComponent } from "../../../standalone/s25.icon.button.component";
import { ResourceMicroService } from "../../../services/resource.micro.service";
import { S25TableComponent } from "../../s25-table/s25.table.component";
import { Proto } from "../../../pojo/Proto";
import { ResourceService } from "../../../services/resource.service";
import { S25Util } from "../../../util/s25-util";

@TypeManagerDecorator("s25-ng-editable-inventory")
@Component({
    selector: "s25-ng-editable-inventory",
    template: `
        @if (init()) {
            <s25-ng-table [dataSource]="tableConfig"></s25-ng-table>
            @if (this.canEdit) {
                <s25-ng-button (clicked)="addRow()" [type]="'primary'">Add Inventory Level</s25-ng-button>
            }
        }
    `,
    styles: `
        :host {
            display: block;
            padding: 1rem;
        }
    `,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class S25EditableInventoryComponent implements OnInit {
    @Input({ required: true }) itemId: string;
    @Input({ required: true }) canEdit: boolean;
    @Input({ required: true }) stock: StockRow[];

    init = signal(false);
    columns: Table.Column[];
    tableConfig: Table.DataSource;

    @ViewChild(S25TableComponent) table: S25TableComponent;

    ngOnInit() {
        this.initTableConfig();
        this.init.set(true);
    }

    initTableConfig() {
        this.columns = [
            { id: "start", header: "Start Date" },
            ...(this.canEdit ? [{ id: "end", header: "End Date" }] : []),
            { id: "qty", header: "Quantity" },
            ...(this.canEdit ? [{ id: "remove", header: "Remove" }] : []),
        ];
        this.tableConfig = {
            type: "unpaginated",
            dataSource: () => Promise.resolve({ rows: this.stock.sort(this.sortStock).map(this.mapToRows) }),
            columns: this.columns,
        };
    }

    //Sort the stock by start date, then by end date, and finally by version number  higher version number comes first
    sortStock(a: StockRow, b: StockRow): number {
        const startA = S25Util.date.toStartOfDay(a.startDt);
        const startB = S25Util.date.toStartOfDay(b.startDt);
        const endA = S25Util.date.toStartOfDay(a.endDt);
        const endB = S25Util.date.toStartOfDay(b.endDt);

        // Check for overlap
        const overlap = S25Util.date.doRangesOverlap(startA, endA, startB, endB);
        if (overlap) {
            // Consider as tie, use version number (itemId) as tiebreaker
            return b.itemId - a.itemId;
        }

        // No overlap, sort by start date
        if (startA < startB) return -1;
        if (startA > startB) return 1;

        // If start dates are equal, sort by end date
        if (endA < endB) return -1;
        if (endA > endB) return 1;

        // As a last resort, use version number
        return b.itemId - a.itemId;
    }

    addRow() {
        const newRow: StockRow = {
            itemId: 0, // Temporary ID, will be replaced after the server is updated
            startDt: new Date().toISOString(),
            endDt: S25Util.date.addDays(new Date(), 1).toISOString(),
            count: 0,
            message: signal("Choose a date range and quantity"),
            isNew: true,
        };

        this.stock.push(newRow);
        this.table.addRow(this.mapToRows(newRow));
    }

    //Add one day to the end date when saving for ANG-4418
    @Bind
    async saveStock(versionNbr: number, stock: StockRow) {
        if (!this.validate(stock)) return;
        stock.itemId = versionNbr;
        const changeRow = this.table.rows.find((row) => row.id === versionNbr);

        stock.isNew = false;
        const [resp, err] = await S25Util.Maybe(ResourceService.updateStockMs(+this.itemId, [stock], []));
        if (err) {
            console.error("Error saving stock:", err);
            return;
        }

        if (resp) {
            const respStock = resp.data.items[0].stock;
            //The service might have updated the stock with a new version number, if so bring that to our local state
            const newStock = respStock.find((resp) => !this.stock.some((s: StockRow) => s.itemId === resp.versionNbr));
            if (newStock?.versionNbr) {
                stock.itemId = newStock.versionNbr;
            }

            changeRow.name = `${stock.startDt}-${stock.endDt}`;
            changeRow.id = stock.itemId; // Update the row ID to the new itemId
        }
    }

    validate(stock: StockRow): boolean {
        if (!stock.message) stock.message = signal("");

        if (!stock.startDt || !stock.endDt) {
            stock.message.set("Start and end dates are required");
            return false;
        }
        if (stock.count === undefined || stock.count < 0) {
            stock.message.set("Quantity must be a non-negative number");
            return false;
        }
        const startDate = S25Util.date.toStartOfDay(stock.startDt);
        const endDate = S25Util.date.toStartOfDay(stock.endDt);
        if (startDate >= endDate) {
            stock.message.set("Start date must be before end date");
            return false;
        }
        stock.message.set(""); // Clear any previous error message
        return true;
    }

    //DB stores dates as exclusive, but we want to display them as inclusive so subtract one day from the end date
    getDisplayEndDate(date: Proto.ISODateString): Date {
        const newDate = S25Util.date.parseDropTZ(date);
        newDate.setDate(newDate.getDate() - 1);
        return newDate;
    }

    async removeStock(stockRow: StockRow) {
        !stockRow.isNew && (await ResourceMicroService.alterStock([+this.itemId], [], [+stockRow.itemId]));
        this.table.removeRow(this.table.rows.find((row) => row.id === stockRow.itemId));
        this.stock = this.stock.filter((s) => s.itemId !== stockRow.itemId);
    }

    @Bind
    mapToRows(stock: StockRow): Table.Row {
        const { count, endDt, itemId, startDt, message } = stock;
        if (!message) stock.message = signal("");
        return {
            id: itemId, //versionNbr is used as the id
            name: `${startDt}-${endDt}`,
            message: stock.message,
            cells: {
                start: {
                    component: S25EditableDateComponent,
                    inputs: {
                        val: startDt,
                        readOnly: !this.canEdit,
                        allowEmpty: false,
                    },
                    outputs: {
                        valChange: (val: Proto.ISODateString) => {
                            stock.startDt = S25Util.date.toStartOfDay(val).toISOString();
                            this.saveStock(stock.itemId, stock);
                        },
                    },
                } satisfies Table.Cell<S25EditableDateComponent>,
                ...(this.canEdit && {
                    end: {
                        component: S25EditableDateComponent,
                        inputs: {
                            val: this.getDisplayEndDate(endDt),
                            readOnly: !this.canEdit,
                        },
                        outputs: {
                            valChange: (val: Proto.ISODateString) => {
                                //Add one day to the end date when saving for ANG-4418
                                const newDate = S25Util.date.toStartOfDay(new Date(val));
                                newDate.setDate(newDate.getDate() + 1);
                                stock.endDt = newDate.toISOString();
                                this.saveStock(stock.itemId, stock);
                            },
                        },
                    },
                }),
                qty: {
                    component: S25EditableNumberComponent,
                    inputs: {
                        val: count,
                        readOnly: !this.canEdit,
                    },
                    outputs: {
                        blurred: (val: number) => {
                            //Add one day to the end date when saving for ANG-4418
                            if (stock.count != val) {
                                stock.count = val;
                                this.saveStock(stock.itemId, stock);
                            }
                        },
                    },
                },
                ...(this.canEdit && {
                    remove: {
                        component: S25IconButtonComponent,
                        inputs: {
                            type: "close",
                            ariaLabel: `Remove stock level`,
                        },
                        outputs: {
                            clicked: () => this.removeStock(stock),
                        },
                    } satisfies Table.Cell<S25IconButtonComponent>,
                }),
            },
        };
    }
}

export type StockRow = Resource.Stock & {
    message?: WritableSignal<string>;
    isNew?: boolean;
};
